// Sprint 17 A6: Duplicate Detection Queue — side-by-side merge
import { useState } from "react";
import {
  GitMerge, X, AlertCircle, CheckCircle2, Users, Mail, Phone, Undo2, Wand2,
} from "lucide-react";
import { Skeleton } from "@/components/skeleton";
import { dismissCrmDuplicate, mergeCrmDuplicate, undoCrmMerge, useCrmDuplicates } from "@/hooks/use-crm-duplicates";
import type { DuplicateCandidate } from "@/hooks/use-crm-duplicates";

interface LastMergeState {
  mergeHistoryId: string;
  label: string;
}

interface ActionToastState {
  tone: "error" | "success";
  message: string;
}

export default function DuplicatesPage() {
  const { data: candidates, isLoading, error, refetch } = useCrmDuplicates();
  const [activeAction, setActiveAction] = useState<{ candidateId: string; kind: "merge" | "dismiss" } | null>(null);
  const [toast, setToast] = useState<ActionToastState | null>(null);
  const [lastMerge, setLastMerge] = useState<LastMergeState | null>(null);
  const [fieldSelections, setFieldSelections] = useState<Record<string, Record<string, "entityA" | "entityB">>>({});

  const pending = (candidates ?? []).filter((c) => c.status === "pending");
  const highConfidence = pending.filter((c) => c.confidence >= 95);
  const reviewMerge = pending.filter((c) => c.confidence >= 80 && c.confidence < 95);
  const reviewQueue = pending.filter((c) => c.confidence < 80);

  const handleMerge = async (candidate: DuplicateCandidate) => {
    setActiveAction({ candidateId: candidate.id, kind: "merge" });
    setToast(null);
    try {
      const result = await mergeCrmDuplicate(candidate.id, {
        entityAId: candidate.entityAId,
        entityBId: candidate.entityBId,
        winnerSource: candidate.recommendedWinner ?? "entityA",
        fieldSelections: fieldSelections[candidate.id],
      });
      setLastMerge({
        mergeHistoryId: result.mergeHistoryId,
        label: `${candidate.entityAName} + ${candidate.entityBName}`,
      });
      refetch();
    } catch (err) {
      setToast({
        tone: "error",
        message: err instanceof Error ? err.message : "Failed to merge duplicate",
      });
    } finally {
      setActiveAction(null);
    }
  };

  const handleUndoMerge = async () => {
    if (!lastMerge) return;

    setToast(null);
    try {
      await undoCrmMerge(lastMerge.mergeHistoryId);
      setLastMerge(null);
      refetch();
    } catch (err) {
      setToast({
        tone: "error",
        message: err instanceof Error ? err.message : "Failed to undo merge",
      });
    }
  };

  const handleDismiss = async (candidateId: string) => {
    setActiveAction({ candidateId, kind: "dismiss" });
    setToast(null);
    try {
      await dismissCrmDuplicate(candidateId);
      refetch();
      setToast({
        tone: "success",
        message: "Marked as not a duplicate.",
      });
    } catch (err) {
      setToast({
        tone: "error",
        message: err instanceof Error ? err.message : "Failed to dismiss duplicate",
      });
    } finally {
      setActiveAction(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-foreground)]">Duplicate Detection</h1>
          <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
            {pending.length} candidates to review
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          <AlertCircle className="h-4 w-4 inline mr-1" />
          {error}
          <button onClick={refetch} className="ml-2 underline">Retry</button>
        </div>
      )}

      {lastMerge && (
        <div className="rounded-md border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-4 py-3 text-sm text-[var(--color-foreground)]">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[var(--color-success)]" />
              <span>
                Merged {lastMerge.label}. Undo is available from merge history while this change is still fresh.
              </span>
            </div>
            <button
              onClick={handleUndoMerge}
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-card)]"
            >
              <Undo2 className="h-3 w-3" />
              Undo merge
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <DuplicatesSkeleton />
      ) : pending.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[var(--color-border)] rounded-lg">
          <Users className="h-12 w-12 mx-auto text-[var(--color-muted-foreground)] mb-3" />
          <p className="text-sm font-medium text-[var(--color-foreground)]">No duplicates found</p>
          <p className="text-xs text-[var(--color-muted-foreground)] mt-1">All contacts are unique</p>
        </div>
      ) : (
        <>
          {highConfidence.length > 0 && (
            <Section
              title="High Confidence"
              subtitle={`${highConfidence.length} candidates (>95% confidence)`}
              accentColor="text-[var(--color-success)]"
            >
              {highConfidence.map((c) => (
                <DuplicateCard
                  key={c.id}
                  candidate={c}
                  onMerge={() => handleMerge(c)}
                  onDismiss={() => handleDismiss(c.id)}
                  activeAction={activeAction?.candidateId === c.id ? activeAction.kind : null}
                  selectedFields={fieldSelections[c.id] ?? {}}
                  onFieldSelectionChange={(fieldKey, source) => {
                    setFieldSelections((current) => ({
                      ...current,
                      [c.id]: {
                        ...(current[c.id] ?? {}),
                        [fieldKey]: source,
                      },
                    }));
                  }}
                />
              ))}
            </Section>
          )}

          {/* Likely duplicates (80-95%) */}
          {reviewMerge.length > 0 && (
            <Section
              title="Likely Duplicates"
              subtitle={`${reviewMerge.length} candidates (80-95% confidence)`}
              accentColor="text-[var(--color-warning)]"
            >
              {reviewMerge.map((c) => (
                <DuplicateCard
                  key={c.id}
                  candidate={c}
                  onMerge={() => handleMerge(c)}
                  onDismiss={() => handleDismiss(c.id)}
                  activeAction={activeAction?.candidateId === c.id ? activeAction.kind : null}
                  selectedFields={fieldSelections[c.id] ?? {}}
                  onFieldSelectionChange={(fieldKey, source) => {
                    setFieldSelections((current) => ({
                      ...current,
                      [c.id]: {
                        ...(current[c.id] ?? {}),
                        [fieldKey]: source,
                      },
                    }));
                  }}
                />
              ))}
            </Section>
          )}

          {/* Review queue (<80%) */}
          {reviewQueue.length > 0 && (
            <Section
              title="Review Queue"
              subtitle={`${reviewQueue.length} candidates (<80% confidence)`}
              accentColor="text-[var(--color-muted-foreground)]"
            >
              {reviewQueue.map((c) => (
                <DuplicateCard
                  key={c.id}
                  candidate={c}
                  onMerge={() => handleMerge(c)}
                  onDismiss={() => handleDismiss(c.id)}
                  activeAction={activeAction?.candidateId === c.id ? activeAction.kind : null}
                  selectedFields={fieldSelections[c.id] ?? {}}
                  onFieldSelectionChange={(fieldKey, source) => {
                    setFieldSelections((current) => ({
                      ...current,
                      [c.id]: {
                        ...(current[c.id] ?? {}),
                        [fieldKey]: source,
                      },
                    }));
                  }}
                />
              ))}
            </Section>
          )}
        </>
      )}

      <ActionToast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}

function Section({ title, subtitle, accentColor, children }: {
  title: string;
  subtitle: string;
  accentColor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div>
        <h3 className={`text-sm font-semibold ${accentColor}`}>{title}</h3>
        <p className="text-xs text-[var(--color-muted-foreground)]">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

function DuplicateCard({
  candidate,
  onMerge,
  onDismiss,
  activeAction,
  selectedFields,
  onFieldSelectionChange,
}: {
  candidate: DuplicateCandidate;
  onMerge?: () => void;
  onDismiss?: () => void;
  activeAction: "merge" | "dismiss" | null;
  selectedFields: Record<string, "entityA" | "entityB">;
  onFieldSelectionChange: (fieldKey: string, source: "entityA" | "entityB") => void;
}) {
  const confidenceColor = candidate.confidence >= 95
    ? "bg-[var(--color-success)]/10 text-[var(--color-success)]"
    : candidate.confidence >= 80
    ? "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
    : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]";

  const isBusy = activeAction !== null;
  const isMerging = activeAction === "merge";
  const isDismissing = activeAction === "dismiss";

  return (
    <div className="stagger-item border border-[var(--color-border)] rounded-lg bg-[var(--color-card)] p-4">
      {/* Confidence badge + match reasons */}
      <div className="flex items-center justify-between mb-3">
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${confidenceColor}`}>
          {candidate.confidence}% match
        </span>
        <span className="text-[10px] text-[var(--color-muted-foreground)]">
          {candidate.matchReasons.map((r) => r.replace(/_/g, " ")).join(" + ")}
        </span>
      </div>

      {/* Side-by-side comparison */}
      <div className="grid grid-cols-2 gap-4 mb-3">
        <ContactCard
          name={candidate.entityAName}
          email={candidate.entityAEmail}
          phone={candidate.entityAPhone}
          label="Contact A"
        />
        <ContactCard
          name={candidate.entityBName}
          email={candidate.entityBEmail}
          phone={candidate.entityBPhone}
          label="Contact B"
        />
      </div>

      {candidate.comparisonFields && candidate.comparisonFields.length > 0 && (
        <div className="mb-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
          <div className="mb-3 flex items-center gap-2">
            <Wand2 className="h-3.5 w-3.5 text-[var(--color-primary)]" />
            <div>
              <p className="text-xs font-semibold text-[var(--color-foreground)]">Field-by-field merge</p>
              <p className="text-[11px] text-[var(--color-muted-foreground)]">
                Choose which value survives on the merged contact record.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {candidate.comparisonFields.map((field) => {
              const selected = selectedFields[field.key] ?? field.recommended;
              return (
                <div key={field.key} className="grid gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
                      {field.label}
                    </span>
                    <span className="text-[10px] text-[var(--color-muted-foreground)]">
                      Recommended: {field.recommended === "entityA" ? "Contact A" : "Contact B"}
                    </span>
                  </div>
                  <div className="grid gap-2 md:grid-cols-2">
                    {[
                      { source: "entityA" as const, label: "Contact A", value: field.entityAValue },
                      { source: "entityB" as const, label: "Contact B", value: field.entityBValue },
                    ].map((option) => {
                      const isSelected = selected === option.source;
                      return (
                        <button
                          key={`${field.key}-${option.source}`}
                          type="button"
                          onClick={() => onFieldSelectionChange(field.key, option.source)}
                          className={`rounded-md border px-3 py-2 text-left text-xs transition-colors ${
                            isSelected
                              ? "border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]"
                              : "border-[var(--color-border)] text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                          }`}
                        >
                          <div className="font-medium">{option.label}</div>
                          <div className="mt-1 text-[11px] text-current/80">{option.value ?? "Empty"}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <button
          onClick={onDismiss}
          disabled={isBusy}
          className="inline-flex items-center gap-1.5 text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] px-3 py-1.5 border border-[var(--color-border)] rounded-md transition-colors"
        >
          <X className="h-3 w-3" /> {isDismissing ? "Dismissing..." : "Not a duplicate"}
        </button>
        <button
          onClick={onMerge}
          disabled={isBusy}
          className="inline-flex items-center gap-1.5 text-xs text-[var(--color-primary-foreground)] bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] px-3 py-1.5 rounded-md disabled:opacity-50 transition-colors"
        >
          <GitMerge className="h-3 w-3" />
          {isMerging ? "Merging..." : "Merge"}
        </button>
      </div>
    </div>
  );
}

function ActionToast({
  toast,
  onDismiss,
}: {
  toast: ActionToastState | null;
  onDismiss: () => void;
}) {
  if (!toast) return null;

  const toneClass = toast.tone === "error"
    ? "border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 text-[var(--color-destructive)]"
    : "border-[var(--color-success)]/20 bg-[var(--color-success)]/5 text-[var(--color-foreground)]";

  return (
    <div className="pointer-events-none fixed bottom-6 right-6 z-50">
      <div
        role="status"
        aria-live="polite"
        className={`pointer-events-auto toast-slide-in flex items-center gap-3 rounded-lg border px-4 py-3 text-sm shadow-lg ${toneClass}`}
      >
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>{toast.message}</span>
        <button
          type="button"
          onClick={onDismiss}
          className="ml-2 text-xs font-medium text-current/80 transition-opacity hover:opacity-100"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function ContactCard({ name, email, phone, label }: {
  name: string;
  email: string;
  phone?: string;
  label: string;
}) {
  return (
    <div className="rounded-lg bg-[var(--color-surface)] p-3">
      <p className="text-[10px] text-[var(--color-muted-foreground)] mb-1">{label}</p>
      <p className="text-sm font-medium text-[var(--color-foreground)]">{name}</p>
      <div className="mt-1 space-y-0.5">
        <p className="text-xs text-[var(--color-muted-foreground)] flex items-center gap-1">
          <Mail className="h-3 w-3" /> {email}
        </p>
        {phone && (
          <p className="text-xs text-[var(--color-muted-foreground)] flex items-center gap-1">
            <Phone className="h-3 w-3" /> {phone}
          </p>
        )}
      </div>
    </div>
  );
}

function DuplicatesSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="border border-[var(--color-border)] rounded-lg p-4">
          <Skeleton className="mb-3 h-4 w-20" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-20 rounded" />
            <Skeleton className="h-20 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}
